import { Injectable } from '@angular/core';
import { BaseService } from '../../shared/base-classes/base.service';
import { AppClientDataService } from '../../shared/services/client-data-service/app-client-data.service';
import {
  EventCategoryCreateUpdateDto,
  EventCategoryFilterDto,
  EventCategoryListResponseDto,
  HeroEventCreateDto,
  HeroEventFilterDto,
  HeroEventListResponseDto,
  HeroEventResponseDto,
  HeroEventUpdateDto,
  IDisplayData,
  PaginationOptionsDto,
  PaginationResultsDto,
  RelationDto,
  ScheduledHeroEventListResponseDto,
  ScheduledHeroEventResponseDto,
  ScheduledHeroEventStudentListItemDto,
  ScheduledHeroEventStudentStatusEnum,
  ScheduledHeroEventUpdateDto,
  StudentScheduledHeroEventCreateDto
} from '@whetstoneeducation/hero-common';
import { IHeroCreateUpdateResolverData } from './hero-event-create-edit/hero-event-create-edit.resolver';
import { IHeroEventListResolverData } from './events-list/hero-event-list-models/hero-event-list-resolver-data.interface';
import { IHeroEventListFilter } from './events-list/hero-event-list-models/hero-event-list-filter.interface';
import { TableFilterOptions } from '../../shared/tables/table-filters/table-filters';
import { TableFiltersPageKeyEnum } from '../../shared/tables/table-filters/table-filters-page-key.enum';

@Injectable({
  providedIn: 'root'
})
export class AppEventService extends BaseService {
  constructor(private appClientDataService: AppClientDataService) {
    super();
  }

  public async getEventCategoriesListResolverData(): Promise<{
    eventCategories: EventCategoryListResponseDto[];
  }> {
    const filters = new EventCategoryFilterDto();
    const eventCategories: EventCategoryListResponseDto[] =
      await this.getEventCategories(filters);
    return {
      eventCategories
    };
  }

  public async getEventCategories(
    filters: EventCategoryFilterDto
  ): Promise<EventCategoryListResponseDto[]> {
    return await this.appClientDataService.execute<
      Promise<EventCategoryListResponseDto[]>
    >(this.GET_ROUTES.EVENT_CATEGORIES_LIST, {
      method: this.METHOD.GET,
      queryParams: this.buildQueryParams(filters, filters.getFields())
    });
  }

  public async getEventCategory(id: number): Promise<RelationDto> {
    return this.appClientDataService.execute<Promise<RelationDto>>(
      this.GET_ROUTES.EVENT_CATEGORY,
      {
        method: this.METHOD.GET,
        pathParams: {
          id
        }
      }
    );
  }

  public async createEventCategory(
    dto: EventCategoryCreateUpdateDto
  ): Promise<RelationDto> {
    return this.appClientDataService.execute<Promise<RelationDto>>(
      this.POST_ROUTES.EVENT_CATEGORY,
      {
        method: this.METHOD.POST,
        body: dto
      }
    );
  }
  public async updateEventCategory(
    id: number,
    dto: EventCategoryCreateUpdateDto
  ): Promise<RelationDto> {
    return this.appClientDataService.execute<Promise<RelationDto>>(
      this.PATCH_ROUTES.EVENT_CATEGORY,
      {
        method: this.METHOD.PATCH,
        body: dto,
        pathParams: {
          id
        }
      }
    );
  }

  public async deleteEventCategory(id: number) {
    return this.appClientDataService.execute(
      this.DELETE_ROUTES.DELETE_EVENT_CATEGORY,
      {
        method: this.METHOD.DELETE,
        pathParams: {
          id
        }
      }
    );
  }

  async getEventCategoryOptions(): Promise<IDisplayData[]> {
    return this.appClientDataService.execute(
      this.GET_ROUTES.EVENT_CATEGORY_OPTIONS,
      {
        method: this.METHOD.GET
      }
    );
  }

  //   Hero Event Requests

  async getHeroEvent(id: number): Promise<HeroEventResponseDto> {
    return this.appClientDataService.execute(this.GET_ROUTES.HERO_EVENT, {
      method: this.METHOD.GET,
      pathParams: {
        id
      }
    });
  }

  async getHeroEvents(
    filter: IHeroEventListFilter
  ): Promise<PaginationResultsDto<HeroEventListResponseDto>> {
    return await this.appClientDataService.execute(
      this.GET_ROUTES.HERO_EVENTS,
      {
        method: this.METHOD.GET,
        queryParams: {
          ...filter.tableFilters,
          active: filter.active,
          ...(filter.eventCategoryId
            ? { eventCategoryId: filter.eventCategoryId }
            : {}),
          ...(filter.search ? { search: filter.search } : {})
        }
      }
    );
  }

  async getHeroEventsResolverData(): Promise<IHeroEventListResolverData> {
    const defaultFilters: IHeroEventListFilter = {
      active: true,
      tableFilters: TableFilterOptions.getPageDefault(
        TableFiltersPageKeyEnum.HERO_EVENT_LIST
      )
    };

    const [heroEvents, eventCategoryOptions] = await Promise.all([
      this.getHeroEvents(defaultFilters),
      this.getEventCategoryOptions()
    ]);

    return {
      defaultFilters,
      heroEvents,
      eventCategoryOptions
    };
  }

  async getEventUpdateResolverData(
    id: number
  ): Promise<IHeroCreateUpdateResolverData> {
    return {
      heroEvent: await this.getHeroEvent(id),
      eventCategories: await this.getEventCategoryOptions()
    };
  }

  async createHeroEvent(heroEventCreateDto: HeroEventCreateDto) {
    return this.appClientDataService.execute<HeroEventResponseDto>(
      this.POST_ROUTES.HERO_EVENT,
      {
        method: this.METHOD.POST,
        body: heroEventCreateDto
      }
    );
  }

  async updateHeroEvent(id: number, herEventUpdateDto: HeroEventUpdateDto) {
    return this.appClientDataService.execute<HeroEventResponseDto>(
      this.PATCH_ROUTES.HERO_EVENT,
      {
        method: this.METHOD.PATCH,
        body: herEventUpdateDto,
        pathParams: { id }
      }
    );
  }

  async addStudentToHeroEvent(id: number, studentId: number) {
    return this.appClientDataService.execute<HeroEventResponseDto>(
      this.PATCH_ROUTES.HERO_EVENT_ADD_STUDENT,
      {
        method: this.METHOD.PATCH,
        pathParams: { id, studentId }
      }
    );
  }

  async deleteHeroEventStudent(id: number, studentId: number) {
    return this.appClientDataService.execute(
      this.DELETE_ROUTES.DELETE_HERO_EVENT_STUDENT,
      {
        method: this.METHOD.DELETE,
        pathParams: {
          id,
          studentId
        }
      }
    );
  }

  async getScheduledHeroEvent(
    id: number
  ): Promise<ScheduledHeroEventResponseDto> {
    return this.appClientDataService.execute<ScheduledHeroEventResponseDto>(
      this.GET_ROUTES.SCHEDULED_HERO_EVENT,
      {
        method: this.METHOD.GET,
        pathParams: {
          id
        }
      }
    );
  }

  async getScheduledHeroEventExport(
    id: number
  ): Promise<ScheduledHeroEventResponseDto> {
    return this.appClientDataService.execute<ScheduledHeroEventResponseDto>(
      this.GET_ROUTES.EXPORT_SCHEDULED_HERO_EVENT,
      {
        method: this.METHOD.GET,
        pathParams: {
          id
        }
      }
    );
  }

  async updateScheduledHeroEvent(
    id: number,
    scheduledEventUpdateDto: ScheduledHeroEventUpdateDto
  ): Promise<ScheduledHeroEventResponseDto> {
    return this.appClientDataService.execute<ScheduledHeroEventResponseDto>(
      this.PATCH_ROUTES.SCHEDULED_HERO_EVENT,
      {
        method: this.METHOD.PATCH,
        pathParams: {
          id
        },
        body: scheduledEventUpdateDto
      }
    );
  }

  async getHistoricalScheduledHeroEventsByHeroEventId(
    heroEventId: number,
    filters: PaginationOptionsDto
  ): Promise<PaginationResultsDto<ScheduledHeroEventListResponseDto>> {
    return this.appClientDataService.execute<
      PaginationResultsDto<ScheduledHeroEventListResponseDto>
    >(this.GET_ROUTES.HISTORICAL_SCHEDULED_HERO_EVENT, {
      method: this.METHOD.GET,
      pathParams: {
        heroEventId
      },
      queryParams: filters
    });
  }

  async createStudentScheduleHeroEvent(
    dto: StudentScheduledHeroEventCreateDto
  ) {
    return this.appClientDataService.execute(
      this.POST_ROUTES.CREATE_STUDENT_SCHEDULED_HERO_EVENT,
      {
        method: this.METHOD.POST,
        body: dto
      }
    );
  }

  getStatusForStudent(
    scheduledHeroEventStudentListItemDto: ScheduledHeroEventStudentListItemDto
  ):
    | ScheduledHeroEventStudentStatusEnum.NOT_AT_EVENT
    | ScheduledHeroEventStudentStatusEnum.AT_EVENT
    | ScheduledHeroEventStudentStatusEnum.LEFT_EVENT {
      
    if (!scheduledHeroEventStudentListItemDto.checkInTimes)
      return ScheduledHeroEventStudentStatusEnum.NOT_AT_EVENT;

    if (
      scheduledHeroEventStudentListItemDto.checkInTimes.length &&
      scheduledHeroEventStudentListItemDto.checkInTimes.length ===
      scheduledHeroEventStudentListItemDto.checkOutTimes.length
    ) {
      return ScheduledHeroEventStudentStatusEnum.LEFT_EVENT;
    } else if (scheduledHeroEventStudentListItemDto.checkInTimes.length) {
      return ScheduledHeroEventStudentStatusEnum.AT_EVENT;
    }
  
    return ScheduledHeroEventStudentStatusEnum.NOT_AT_EVENT;
  }
  
  async checkInOutEventStudent(scheduledHeroEventStudentListItem: ScheduledHeroEventStudentListItemDto, scheduledHeroEventId: number): Promise<void> {
    if (!scheduledHeroEventStudentListItem.id) {
      const newStudentScheduledEvent = await this.createStudentScheduleHeroEvent(
        new StudentScheduledHeroEventCreateDto({
          studentId: scheduledHeroEventStudentListItem.studentId,
          scheduledHeroEventId
        })
      );
      return;
    }
  
    const status = this.getStatusForStudent(scheduledHeroEventStudentListItem);
    let scheduledEvent: ScheduledHeroEventUpdateDto;
    if (status === ScheduledHeroEventStudentStatusEnum.AT_EVENT) {
      scheduledEvent = new ScheduledHeroEventUpdateDto({
        checkOutStudentScheduleHeroEventId:
          scheduledHeroEventStudentListItem.id
      })
    } else {
      scheduledEvent = new ScheduledHeroEventUpdateDto({
        checkInStudentScheduleHeroEventIds: [
          scheduledHeroEventStudentListItem.id
        ]
      })
    }
  
    await this.updateScheduledHeroEvent(
      scheduledHeroEventId,
      scheduledEvent
    );
  }
}
